import type { CognitoAuthStorage } from 'amazon-cognito-auth-js';

/**
 * In-memory implementation of the storage contract `amazon-cognito-auth-js`
 * reads session data through (`CognitoAuth`'s optional `Storage` config). The
 * SDK persists the ID, access, and — most sensitively — refresh tokens via this
 * object; the default is `window.localStorage`. Backing it with a plain object
 * instead keeps every Cognito token in JS heap only, so none is readable from
 * localStorage/sessionStorage and nothing auth-related survives a reload or
 * tab close.
 *
 * Tradeoff this encodes: because the store starts empty on every page load,
 * `FederatedAuth` finds no cached session and re-runs the Hosted-UI OAuth code
 * grant. The Cognito web session cookie on the Hosted-UI domain usually makes
 * that redirect silent (no credential prompt), but it is a real navigation
 * round-trip per reload and per new tab. That is the accepted cost of persisting
 * nothing at rest.
 */
export class InMemoryCognitoStorage implements CognitoAuthStorage {
  private data: Record<string, string> = {};

  getItem(key: string): string | null {
    return Object.prototype.hasOwnProperty.call(this.data, key)
      ? this.data[key]
      : null;
  }

  setItem(key: string, value: string): void {
    // The SDK stores only strings; coerce defensively so a stray non-string
    // (e.g. a number) round-trips as the string the getters expect.
    this.data[key] = String(value);
  }

  removeItem(key: string): void {
    delete this.data[key];
  }
}

/**
 * Whether a Cypress runner is driving the page. Cypress sets `window.Cypress` on
 * the application-under-test window, and nothing else does, so this is false in
 * production and for real users — the same signal the RUM guard in
 * `src/index.tsx` keys on.
 */
function isUnderCypress(): boolean {
  return (
    typeof window !== 'undefined' &&
    (window as Window & { Cypress?: unknown }).Cypress !== undefined
  );
}

/**
 * The store the Cognito SDK reads and writes session tokens through.
 *
 * Production and real users get {@link InMemoryCognitoStorage}: nothing
 * auth-related is persisted, and a reload re-runs the Hosted-UI OAuth code grant
 * (the deliberate "persist nothing at rest" tradeoff described above). A single
 * shared instance, because `FederatedAuth` constructs a new `CognitoAuth` on each
 * mount (see its `useEffect`/`login`) and they must share one store for a session
 * established on one mount to be visible to the next within the same page load.
 *
 * Under Cypress ONLY, back the SDK with `window.localStorage` instead. The
 * deployed integration suite reloads the page in every test's `beforeEach`
 * (`cy.visit`); with the in-memory store each reload finds no session and re-runs
 * the full Cognito → Midway redirect. That is both a ~20–30s per-test cost and an
 * intermittent race — the bootstrap `GetUserProfile` query can fire before the
 * re-auth seeds a JWT, so AppSync answers 401, the app renders "Couldn't load
 * your profile" with no app shell, the suite's `waitForShell` fails, and across
 * retries the whole run exceeds the Hydra timeout and is reported INVALID.
 * Persisting the session to `localStorage` lets the first sign-in survive reloads
 * (and, via cypress-localstorage-commands' save/restoreLocalStorage, across spec
 * files), so the redirect happens once rather than on every test. Gated to
 * Cypress so production keeps persisting nothing at rest — mirrors the
 * `window.Cypress` RUM guard in `src/index.tsx`.
 */
export const cognitoTokenStorage: CognitoAuthStorage = isUnderCypress()
  ? window.localStorage
  : new InMemoryCognitoStorage();
